// src/components/onboarding/useReadToEndGate.js
import { useCallback, useEffect, useRef, useState } from "react";

// How long the end marker must stay in view to count as read (spec § Agreement).
export const READ_DWELL_MS = 400;

// The default measurement: is the end marker inside the viewport, and does
// the whole page fit without scrolling? The web page scrolls the window
// itself and its only sticky element beside the text is the side card, so
// "in view" is the viewport. Injectable (`measure`) because jsdom has no
// layout: tests hand the hook a fake with the answers they need.
export function measureMarker(marker) {
  const rect = marker.getBoundingClientRect();
  const viewportBottom =
    window.innerHeight || document.documentElement.clientHeight;
  return {
    visible: rect.top < viewportBottom && rect.bottom > 0,
    fits: document.documentElement.scrollHeight <= viewportBottom,
  };
}

// The read-to-end gate (spec § Agreement, "Gate rule (mechanical)").
// Reached when the end marker:
//   - has been in view for READ_DWELL_MS without leaving it (gateMode
//     'marker_reached'), or
//   - receives focus, or the End key is pressed (Ctrl+End included) —
//     'marker_reached' too, or
//   - the whole text fits at first paint — 'fit_at_first_paint' — re-tested
//     on every resize and every change of `layoutKey` (tick, name, layout).
// Once reached it stays reached. `reachedAt` is a `performance.now()`
// monotonic stamp, not a wall-clock instant (ruling R151/R161/R172): the
// server accepts endMarkerReachedAt only between serverNow and serverNow + a
// few minutes, so the client must never send its own Date/clock, which can be
// arbitrarily wrong in either direction. The page pairs this stamp with
// another one taken when the view (and its serverNow, this response's own
// instant — not servedAt, which can be days old for a returning reader) was
// received, and sends `serverNow + (reachedAt − loadedAt)` — real elapsed
// time, regardless of what the browser's clock says. Whether the client may
// sign (reached AND ticked AND a name) is the page's decision.
export function useReadToEndGate({
  markerRef,
  measure = measureMarker,
  dwellMs = READ_DWELL_MS,
  layoutKey,
}) {
  const [gate, setGate] = useState({
    reached: false,
    gateMode: null,
    reachedAt: null,
  });
  const reachedRef = useRef(false);
  const dwell = useRef(null);

  const stopDwell = () => {
    clearTimeout(dwell.current);
    dwell.current = null;
  };

  const unlock = useCallback((gateMode) => {
    if (reachedRef.current) return;
    reachedRef.current = true;
    stopDwell();
    setGate({ reached: true, gateMode, reachedAt: performance.now() });
  }, []);

  const check = useCallback(
    (allowFit) => {
      const marker = markerRef.current;
      if (reachedRef.current || !marker) return;
      const m = measure(marker);
      if (allowFit && m.fits) {
        unlock("fit_at_first_paint");
        return;
      }
      if (!m.visible) {
        stopDwell();
        return;
      }
      if (dwell.current == null) {
        dwell.current = setTimeout(() => {
          dwell.current = null;
          const now = markerRef.current && measure(markerRef.current);
          if (now && now.visible) unlock("marker_reached");
        }, dwellMs);
      }
    },
    [markerRef, measure, dwellMs, unlock],
  );

  // First paint, and every layout change the page reports.
  useEffect(() => {
    check(true);
  }, [check, layoutKey]);

  useEffect(() => {
    const onScroll = () => check(false);
    const onResize = () => check(true);
    const onKeyDown = (e) => {
      if (e.key === "End") unlock("marker_reached");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKeyDown);
      stopDwell();
    };
  }, [check, unlock]);

  const onMarkerFocus = useCallback(() => unlock("marker_reached"), [unlock]);

  return { ...gate, onMarkerFocus };
}

// prefers-reduced-motion, read once per mount (matchMedia is absent in jsdom
// and in very old browsers: no preference then).
export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
